import { types } from "mobx-state-tree"

/**
 * Dropdown Option Model
 * Represents a single dropdown option with its configuration
 */
export const DropdownOption = types.model("DropdownOption", {
  id: types.identifier,
  label: types.string,
  disabled: types.optional(types.boolean, false),
})

/**
 * NavbarDropdownStore
 * MobX State Tree store for managing navbar dropdown state
 *
 * Features:
 * - Controlled selected option state
 * - Option selection callback support
 * - Prevents unnecessary re-renders through observer pattern
 * - Type-safe option management
 */
export const NavbarDropdownStore = types
  .model("NavbarDropdownStore", {
    options: types.array(DropdownOption),
    selectedOption: types.maybe(types.string),
    placeholder: types.optional(types.string, "Select..."),
  })
  .views((self) => ({
    /**
     * Get the currently selected option
     */
    get currentOption() {
      return self.options.find((opt) => opt.id === self.selectedOption)
    },

    /**
     * Get the display label for the current selection
     */
    get displayLabel() {
      return this.currentOption?.label || self.placeholder
    },

    /**
     * Check if a specific option is selected
     */
    isOptionSelected(optionId: string) {
      return self.selectedOption === optionId
    },

    /**
     * Get option by ID
     */
    getOptionById(optionId: string) {
      return self.options.find((opt) => opt.id === optionId)
    },

    /**
     * Get all enabled options
     */
    get enabledOptions() {
      return self.options.filter((opt) => !opt.disabled)
    },
  }))
  .actions((self) => ({
    /**
     * Set the selected option
     */
    setSelectedOption(optionId: string) {
      const option = self.getOptionById(optionId)
      if (option && !option.disabled) {
        self.selectedOption = optionId
      }
    },

    /**
     * Set the placeholder text
     */
    setPlaceholder(placeholder: string) {
      self.placeholder = placeholder
    },

    /**
     * Add a new option
     */
    addOption(option: { id: string; label: string; disabled?: boolean }) {
      self.options.push(DropdownOption.create(option))
    },

    /**
     * Set all options at once
     */
    setOptions(options: Array<{ id: string; label: string; disabled?: boolean }>) {
      // biome-ignore lint/suspicious/noExplicitAny: MST replace() requires any type
      self.options.replace(options.map((opt) => DropdownOption.create(opt)) as any)
    },

    /**
     * Remove an option by ID
     */
    removeOption(optionId: string) {
      const index = self.options.findIndex((opt) => opt.id === optionId)
      if (index !== -1) {
        self.options.splice(index, 1)
      }
    },

    /**
     * Toggle option disabled state
     */
    toggleOptionDisabled(optionId: string) {
      const option = self.getOptionById(optionId)
      if (option) {
        option.disabled = !option.disabled
      }
    },

    /**
     * Clear all options
     */
    clearOptions() {
      self.options.clear()
      self.selectedOption = undefined
    },

    /**
     * Reset to initial state
     */
    reset() {
      self.selectedOption = undefined
    },
  }))

export type NavbarDropdownStoreType = typeof NavbarDropdownStore.Type
export type DropdownOptionType = typeof DropdownOption.Type
